import { useEffect } from 'react';

import { useRouter } from 'next/router';

import { setSrpPathInSessionStorage, srpPatterns } from '@/utilities/srpPathFromSessionStorageUtils';

/**
 * Custom hook that manages SRP navigation state and scroll position restoration.
 *
 * This hook automatically:
 * - Detects when the user is on an SRP page using URL pattern matching
 * - Captures the SRP path when navigating away
 *
 * The hook uses Next.js router events to track navigation and session storage
 * to persist data across page transitions. It only activates on pages that
 * match known SRP URL patterns.
 *
 * @hook
 * @returns {void} This hook doesn't return any values - it manages side effects only
 */
const useLastSrpUrl = () => {
    const { asPath, basePath, events } = useRouter();
    const completePath = [basePath, asPath].join('');
    const isSrpUrl = srpPatterns.some((pattern) => pattern.test(completePath));

    /**
     * Effect that captures SRP data when navigating away from SRP pages.
     * Listens to 'routeChangeStart' to store the current SRP path and scroll position
     * before the user leaves the page.
     */
    useEffect(() => {
        const handleHistoryChange = () => {
            if (!isSrpUrl) return;
            setSrpPathInSessionStorage({ path: asPath });
        };

        events.on('beforeHistoryChange', handleHistoryChange);
        return () => events.off('beforeHistoryChange', handleHistoryChange);
    }, [events, asPath, isSrpUrl]);
};

export default useLastSrpUrl;
